import { safeDifferenceInCalendarDays } from 'shared/util/datetime-fns';
import { scrollToElementById } from '@/utils/scroll';
import { focusElementById } from '@/utils/focus';

import type { Filters, HotelOffer, HotelReviews, RoomWithPeople } from '@/interfaces';
import { StarRating } from '@/components/starRating/StarRating';
import { ShareBar } from '../shareBar/ShareBar';
import { ExpandableTextPanel } from '@/components/expandableTextPanel/ExpandableTextPanel';
import { BestOfferDetails } from './components/bestOfferDetails/BestOfferDetails';
import { HotelPageGuestRating } from './components/hotelPageGuestRating/HotelPageGuestRating';
import { HotelFeatures } from './components/hotelFeatures/HotelFeatures';
import { EnvironmentalCertificate } from './components/environmentalCertificate/EnvironmentalCertificate';
import { MapImageButton } from '@/components/mapImageButton/MapImageButton';

interface Props {
    hotelKey: string;
    hotelName: string;
    description: string;
    starRating: number;
    address?: string;
    placeName: string;
    bestOffer?: HotelOffer;
    reviews?: HotelReviews;
    features: readonly number[] | readonly string[];
    filters: Filters;
    latitude?: number;
    longitude?: number;
}

export const HotelData = ({
    hotelKey,
    hotelName,
    description,
    starRating,
    address,
    placeName,
    bestOffer,
    reviews,
    features,
    filters,
    latitude,
    longitude,
}: Props) => {
    const nightsSearched = safeDifferenceInCalendarDays(filters.checkInDate, filters.checkOutDate);
    const numberOfAdults = filters.rooms.reduce((sum: number, room: RoomWithPeople): number => sum + room.adults, 0);
    const numberOfChildren = filters.rooms.reduce((sum: number, room: RoomWithPeople): number => sum + room.children.length, 0);
    return (
        <div className="hotel-data">
            <div className="main-data" data-testid="HotelData-mainData">
                <div className="flex flex-row flex-justify-space-between--lt768 flex-align-center u-mb16">
                    <ShareBar hotelKey={hotelKey} hotelName={hotelName} filters={filters} />
                </div>
                <StarRating id="HotelRating" rating={starRating} bigStars extraSpacing />
                <div className="grid">
                    <div className="grid__unit u-size1of1--gt768 u-size1of2--lt768">
                        <h1 className="u-t3--lt768 u-strong--lt768">{hotelName}</h1>
                        <div className="address-text">{address ? `${address}, ${placeName}` : placeName}</div>
                    </div>
                    <BestOfferDetails
                        offer={bestOffer}
                        nights={nightsSearched}
                        numberOfAdults={numberOfAdults}
                        numberOfChildren={numberOfChildren}
                        noPanel
                    />
                </div>
                <div className="u-hide-gt768">
                    <HotelPageGuestRating {...reviews} />
                </div>
                {description && description.length > 0 && <ExpandableTextPanel text={description} className="u-mv16" />}
                <HotelFeatures features={features} />
            </div>
            <div className="desktop-data" data-testid="HotelData-desktopData">
                <BestOfferDetails
                    offer={bestOffer}
                    nights={nightsSearched}
                    numberOfAdults={numberOfAdults}
                    numberOfChildren={numberOfChildren}
                />
                <MapImageButton
                    buttonText="Vis i kart"
                    className="u-ml16 u-mb16 hotel-page-map-button"
                    onClick={() => {
                        scrollToElementById('hotel-page-map');
                        focusElementById('hotel-page-map');
                    }}
                    latitude={latitude}
                    longitude={longitude}
                    zoom={14}
                    showPin
                />
                <HotelPageGuestRating {...reviews} />
                <EnvironmentalCertificate hotelKey={hotelKey} className="u-mr0 u-mb0" />
            </div>
        </div>
    );
};
